import React, { useState, useEffect } from 'react';
import './LocationDetector.css';

const LocationDetector = ({ fields, onFieldDetected }) => {
  const [location, setLocation] = useState(null);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [detectionError, setDetectionError] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  // Detect field based on location
  const detectField = (location) => {
    // This is a simplified field detection algorithm
    // In a real app, this would use proper geofencing and polygon containment algorithms
    
    if (!fields || !location) return null;
    
    // Find field containing point (simplified version)
    // In reality, this would use proper polygon containment checks
    for (const field of fields) {
      if (field.boundaries && field.boundaries.length > 0) {
        // Check if point is inside field boundaries
        // This is a very simplified version
        const isInside = isPointInPolygon(
          { lat: location.latitude, lng: location.longitude },
          field.boundaries
        );
        
        if (isInside) {
          return field;
        }
      }
    }
    
    return null;
  };
  
  // Simple point-in-polygon algorithm (ray casting)
  const isPointInPolygon = (point, polygon) => {
    if (!polygon || polygon.length < 3) return false;
    
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lat;
      const yi = polygon[i].lng;
      const xj = polygon[j].lat;
      const yj = polygon[j].lng;
      
      const intersect = ((yi > point.lng) !== (yj > point.lng)) &&
        (point.lat < (xj - xi) * (point.lng - yi) / (yj - yi) + xi);
      
      if (intersect) inside = !inside;
    }
    
    return inside;
  };

  // Start location detection
  const startDetection = () => {
    setDetectingLocation(true);
    setDetectionError(null);
    
    if (!navigator.geolocation) {
      setDetectionError('Geolocation is not supported by your browser');
      setDetectingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const currentLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        
        setLocation(currentLocation);
        
        // Detect field
        const detectedField = detectField(currentLocation);
        if (detectedField) {
          setSelectedField(detectedField);
          onFieldDetected(detectedField);
        } else {
          setSelectedField(null);
        }
        
        setDetectingLocation(false);
      },
      (error) => {
        let errorMessage;
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location access denied. Please allow location access.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out.";
            break;
          default:
            errorMessage = "An unknown error occurred.";
        }
        setDetectionError(errorMessage);
        setDetectingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  useEffect(() => {
    // Auto-detect on component mount
    startDetection();
    
    // Set up interval for continuous detection
    const detectionInterval = setInterval(() => {
      startDetection();
    }, 60000); // Check every minute
    
    return () => {
      clearInterval(detectionInterval);
    };
  }, [fields]); // Re-run when fields change

  return (
    <div className="location-detector">
      <div className="location-status">
        {detectionError && (
          <div className="location-error">
            <span className="error-icon">⚠️</span>
            <span>{detectionError}</span>
          </div>
        )}
        
        {!detectionError && (
          <>
            <div className="status-indicator">
              <span className={`status-dot ${detectingLocation ? 'detecting' : (selectedField ? 'detected' : 'undetected')}`}></span>
              <span className="status-text">
                {detectingLocation 
                  ? 'Detecting location...' 
                  : (selectedField 
                      ? `Field detected: ${selectedField.name || selectedField.id}`
                      : 'No field detected at current location'
                    )
                }
              </span>
            </div>
            
            <button 
              className="refresh-location-btn"
              onClick={startDetection}
              disabled={detectingLocation}
            >
              {detectingLocation ? 'Detecting...' : 'Refresh Location'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default LocationDetector; 